import { useQueryClient } from '@tanstack/react-query'
import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from 'ag-grid-community'
import { useMemo, useState } from 'react'

const cachedRowCountMap = new Map<string, number>()

export function clearRowCountCache() {
  cachedRowCountMap.clear()
}

type FetchFunction<T, F> = (params: {
  filters: F
  search: string
  request: IServerSideGetRowsRequest
}) => Promise<{ rowData: T[]; rowCount: number; [key: string]: unknown }>

export function useServerSideDataSource<T, F>(
  filters: F | null,
  search: string,
  fetchFn: FetchFunction<T, F>,
) {
  const queryClient = useQueryClient()
  const [isFetching, setIsFetching] = useState(false)
  const [currRowCount, setCurrRowCount] = useState<number>()
  const [extras, setExtras] = useState<Record<string, unknown>>({})

  const serverSideDatasource = useMemo<IServerSideDatasource>(
    () => ({
      getRows: async (params: IServerSideGetRowsParams<T>) => {
        // No filters yet (not ready) -> nothing to fetch.
        if (filters === null) {
          params.success({ rowData: [], rowCount: 0 })
          return
        }
        const cacheKey = JSON.stringify({
          filters,
          search,
          rowGroupCols: params.request.rowGroupCols,
          groupKeys: params.request.groupKeys,
          filterModel: params.request.filterModel,
        })
        const requestParams = {
          filters,
          search,
          request: {
            ...params.request,
            includeCount: !cachedRowCountMap.has(cacheKey),
          },
        }

        try {
          setIsFetching(true)
          const data = await queryClient.fetchQuery({
            queryKey: [fetchFn.name, requestParams],
            queryFn: async () => fetchFn(requestParams),
            staleTime: 5000,
          })

          const { rowData, rowCount, ...rest } = data ?? {}
          if (Object.keys(rest).length) setExtras(rest)

          // Cache row count to improve performance
          if (rowCount !== -1) {
            cachedRowCountMap.set(cacheKey, rowCount)
            setCurrRowCount(rowCount)
          }

          if (rowData) {
            let resolvedCount = cachedRowCountMap.get(cacheKey) ?? -1
            // Never report a total smaller than the rows we actually received
            // for this page. A stale/zero cached count (e.g. cached when the
            // filter returned nothing, before the data existed) would make AG
            // Grid SSRM treat the total as authoritative and discard the rows
            // we just delivered — rendering an empty grid. Drop the stale entry
            // so the next fetch re-counts, and report "unknown" (-1) so the
            // grid keeps (and correctly counts) the returned rows.
            const lastRowThisPage =
              (params.request.startRow ?? 0) + rowData.length
            if (resolvedCount !== -1 && resolvedCount < lastRowThisPage) {
              cachedRowCountMap.delete(cacheKey)
              resolvedCount = -1
            }
            params.success({
              rowData,
              rowCount: resolvedCount,
            })
            // AG Grid needs the -1 sentinel above, but the control bar would
            // render it literally as "-1 Results" -- leave the count off
            // entirely when we don't have a trustworthy one.
            setCurrRowCount(resolvedCount === -1 ? undefined : resolvedCount)
          }
        } catch (error) {
          console.error('Failed to fetch data:', error)
          params.fail()
        } finally {
          setIsFetching(false)
        }
      },
    }),
    [filters, queryClient, search, fetchFn],
  )

  return {
    serverSideDatasource,
    isFetching,
    rowCount: currRowCount,
    cachedRowCountMap,
    extras,
  }
}
